import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {WeblinkData} from '../shared/weblink-data/weblink-data';
import {Activity} from '../../core/activity/activity';
import {EditableFault, FaultState} from '../shared/editable-fault/editable-fault';
import {Token} from './token/token';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {WeblinkAttachmentFile} from './weblink-attachment/weblink-attachment-file';
import {Jwt} from './jwt/jwt';
import {WeblinkAttachment} from './weblink-attachment/weblink-attachment';
import {ConfigService} from 'src/app/config/config.service';
import {Version} from "../../core/version/version";
import {catchError, map, Observable, of, throwError} from "rxjs";


@Injectable()
export class WeblinkFaultService {
  private weblinkUrl = '/weblink';
  private faultUrl = '/faults/';
  private activityUrl = '/activities/';
  private attachmentUrl = '/attachments';
  private validateUrl = '/weblink/validate';

  private uploadQueue: Array<EditableFault> = [];

  private attachments: { [id: number]: WeblinkAttachmentFile; } = [];
  private thumbnails: { [id: number]: WeblinkAttachmentFile; } = [];

  token: Jwt;
  private tokenObject: Token;

  private uploadCounter = 0;

  attachmentAdded = new EventEmitter<{ fault: EditableFault, attachment: WeblinkAttachment }>();


  constructor(private httpClient: HttpClient, private sanitizer: DomSanitizer, private configService: ConfigService) { }

  isValidToken(token: Jwt): Observable<{ IsValid: boolean, ValidTo: Date }> {
    if (this.token === token && this.tokenObject) {
      return of({ IsValid: this.tokenObject.IsValid && this.tokenObject.IsSignatureValid && !this.tokenObject.IsExpired, ValidTo: this.tokenObject.ValidTo });
    } else {
      this.token = token;
      const endpoint = this.getValidateUrl(token);
      return this.httpClient
        .get(endpoint, { headers: this.getDefaultHeaders() })
        .pipe(
          map(res => { return res as Token; }),
          map((resToken: Token) => {
            this.token = token;
            this.tokenObject = resToken;

            //console.log(resToken);

            if (resToken.IsValid && resToken.IsSignatureValid && !resToken.IsExpired) {
              return { IsValid: true, ValidTo: resToken.ValidTo };
            } else {
              this.token = null;
              return { IsValid: false, ValidTo: resToken.ValidTo };
            }
          }),
          catchError((e) => this.handleError(e))
        );
    }
  }

  getWeblinkFaults(): Observable<WeblinkData> {
    const endpoint = this.getWeblinkUrl();
    return this.httpClient
      .get<WeblinkData>(endpoint, { headers: this.getWeblinkHeaders() })
      .pipe(
        catchError((e) => this.handleError(e))
      );
  }

  getWeblinkAttachment(attachmentID: number, height: number = 0, width: number = 0): Observable<WeblinkAttachmentFile> {
    if (attachmentID !== null) {
      let httpParams = new HttpParams();
      httpParams = httpParams.set('height', height.toString());
      httpParams = httpParams.set('width', width.toString());
      // httpParams = httpParams.set('originalWithScribbles', true);    /* THIS PARAMETER IS NOT IN USE --> FIX IN API ENDPOINT CHANGED! */

      const endpoint = this.getAttachmentUrl(attachmentID);
      let attachment: WeblinkAttachmentFile
      if (height == 240)
        attachment = this.thumbnails[attachmentID];
      else if (height == 0)
        attachment = this.attachments[attachmentID];

      if (attachment != null)
        return of(attachment);

      return this.httpClient
        .get<SafeUrl>(endpoint, { headers: this.getAttachmentHeaders(), params: httpParams, responseType: 'blob' as 'json' })
        .pipe(
          map((blob: Blob) => {
            const urlCreator = window.URL;
            attachment = new WeblinkAttachmentFile();
            attachment.MimeType = blob.type;
            const url = urlCreator.createObjectURL(blob)
            attachment.u = url;
            attachment.b = blob;
            attachment.Url = this.sanitizer.bypassSecurityTrustUrl(url);
            if (height == 240)
              this.thumbnails[attachmentID] = attachment;
            else if (height == 0)
              this.attachments[attachmentID] = attachment;
            return attachment;
          }),
          catchError((e) => this.handleError(e))
        );
    } else {
      return of(null);
    }
  }

  // create new activity by fault
  createFaultActivity(faultId: number, activity: Activity): Observable<Activity> {
    // already uploaded
    if (activity.Id) {
      return of<Activity>(activity);
    } else {
      const endpoint = this.getFaultActivityUrl(faultId);
      return this.httpClient
        .post<Activity>(endpoint, JSON.stringify(activity), { headers: this.getWeblinkHeaders() })
        .pipe(catchError(e => this.handleError(e)));
    }
  }

  // createActivityAttachmentWithProgress(activityID: number, file: File): Observable<Attachment> {
  //   const endpoint = this.getActivityAttachmentUrl(activityID);
  //   const req = new HttpRequest('POST', endpoint, file, {
  //     reportProgress: true,
  //   });
  //   this.httpClient.request(req).subscribe(event => {
  //     if (event.type === HttpEventType.UploadProgress) {
  //       // This is an upload progress event. Compute and show the % done:
  //       const percentDone = Math.round(100 * event.loaded / event.total);
  //       console.log(`File is ${percentDone}% uploaded.`);
  //     } else if (event instanceof HttpResponse) {
  //       console.log('File is completely uploaded!');
  //     }
  //   });
  // }

  version() {
    const endpoint = this.getVersionUrl(this.token);
    return this.httpClient.get(endpoint, { headers: this.getDefaultHeaders() })
      .pipe(
        map((version: Version) => {
          return version;
        })
      );
  }

  // create new attachment by activity
  createActivityAttachment(activityID: number, file: File, description: string): Observable<WeblinkAttachment> {
    const endpoint = this.getActivityAttachmentUrl(activityID);
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('file_description', description);

    //, responseType: 'text'
    return this.httpClient
      .post(endpoint, formData, { headers: this.getFileHeaders() })
      .pipe(
        map((attachmentId: number) => {

          return <WeblinkAttachment>{ Id: attachmentId, Type: file.type.startsWith("image") ? 0 : 1, Description: file.name, MimeType: file.type };
        }),
        catchError((e) => {
          return this.handleError(e);
        })
      );
  }

  // protected getHeaders(): Headers {
  //   const headers = this.getDefaultHeaders();
  //   const authHeader = 'Token JYm52iXENNof81xOMLJSC3NYsijbtdxr0bu7nSvbBIKeV';
  //   const databaseId = '65911EA4-58AC-4D77-9FF7-9FE1AAB2A666';
  //   const schema = 'TEST_QS';

  //   headers.append('Authorization', authHeader);
  //   headers.append('Edr-Database-Id', databaseId);
  //   headers.append('Edr-Schema', schema);

  //   return headers;
  // }

  protected getAttachmentHeaders(): HttpHeaders {
    let headers: HttpHeaders = new HttpHeaders();
    const authHeader = 'Bearer ' + this.token.jwt;
    headers = headers.append('Authorization', authHeader);
    headers = headers.append('Content-Type', 'image/jpg');
    return headers;
  }

  protected getWeblinkHeaders(): HttpHeaders {
    let headers: HttpHeaders = this.getDefaultHeaders();
    const authHeader = 'Bearer ' + this.token.jwt;
    headers = headers.append('Authorization', authHeader);
    return headers;
  }

  protected getFileHeaders(): HttpHeaders {
    let headers: HttpHeaders = this.getDefaultFileHeaders();
    const authHeader = 'Bearer ' + this.token.jwt;
    headers = headers.append('Authorization', authHeader);
    return headers;
  }

  private getFaultActivityUrl(faultId: number) {
    return this.getFullUrl(this.weblinkUrl + this.faultUrl + faultId + this.activityUrl);
  }

  private getValidateUrl(token: Jwt) {
    return this.getFullUrl(this.validateUrl + '?jwt=' + token.jwt);
  }

  private getVersionUrl(token: Jwt) {
    return this.getFullUrl('/version');
  }


  private getWeblinkUrl() {
    return this.getFullUrl(this.weblinkUrl);
  }

  private getAttachmentUrl(attachmentId: number) {
    return this.getFullUrl(this.weblinkUrl + this.attachmentUrl + '/' + attachmentId);
  }

  private getActivityAttachmentUrl(activityId: number) {
    return this.getFullUrl(this.weblinkUrl + this.activityUrl + activityId + this.attachmentUrl);
  }

  protected getFullUrl(appRelativeEndpoint: string): string {
    if (this.token.api) {
      return this.token.api + appRelativeEndpoint;
    } else {
      return this.configService.getConfig().apiBaseUrl + appRelativeEndpoint;
    }
  }

  protected getErrorMessage(error: HttpErrorResponse | any): string {
    let errMsg: string;

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        errMsg = 'Keine Verbindung zum Server!';
      } else {
        const body: HttpErrorResponse | any = error || {};
        if (body.error) {
          const jsonError = JSON.parse(body.error);
          errMsg = jsonError.ExceptionMessage || jsonError.Message;
        } else {
          errMsg = body.message;
        }
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }

    return errMsg;
  }

  protected handleError(error: HttpErrorResponse | any) {
    let errMsg: string;

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return throwError('Keine Verbindung zum Server!');
      } else {
        const body: HttpErrorResponse | any = error || {};
        if (body.error) {
          const jsonError = JSON.parse(body.error);
          errMsg = jsonError.ExceptionMessage || jsonError.Message;
        } else {
          errMsg = body.message;
        }
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }

    return throwError(errMsg);
  }

  getDefaultHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' });
  }

  getDefaultFileHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Accept': 'application/json' });
  }

  addFaultsToQueue(faults: EditableFault[]) {
    faults.forEach(fault => this.uploadQueue.push(fault));
    this.startUpload();
  }

  startUpload() {
    const fault = this.uploadQueue.pop();
    if (fault) {
      this.uploadFault(fault);
    } else {
    }
  }


  uploadFault(fault: EditableFault) {
    fault.faultState = FaultState.uploadingActivity;
    this.createFaultActivity(fault.Id, fault.Activities[0]).subscribe(
      async (activity) => {
        fault.Activities = [];
        fault.Activities.push(activity);
        fault.IsEditable = false;
        fault.isSelected = false;

        if (fault.newFiles) {
          fault.faultState = FaultState.uploadingAttachment;

          let faultState: FaultState = FaultState.synced;

          for (const newFile of fault.newFiles) {
            const description = `${activity.Status?.Name2 ?? activity.Status.Name1}: ${newFile.name.slice(0, newFile.name.lastIndexOf('.'))}`;

            const att = await this.createActivityAttachment(activity.Id, newFile, description)
              .toPromise()
              .catch(error => {
                console.log(error);
                faultState = FaultState.uploadErrorAttachment;
              });

            if (att) {
              if (!fault.Files) {
                fault.Files = [];
              }

              fault.Files.push(att);
              this.attachmentAdded.emit({ fault, attachment: att })
            }
          }

          if (faultState === FaultState.synced) {
            this.startUpload();
          }
          fault.faultState = faultState;


        } else {
          fault.faultState = FaultState.synced;
          this.startUpload();
        }
      },
      error => {
        console.log(error);
        fault.faultState = FaultState.uploadErrorActivity;
      });
  }


}
